import { useToasts } from '@/hooks/useToasts'
import { useTranslation } from 'react-i18next'

import { ApolloError } from '@apollo/client'

import {
  useArchiveFloorMutation,
  useUnarchiveFloorMutation,
} from '@/graphql/generated/hooks'

interface ArchiveFloorArgsIProps {
  floorId: string
  newDefaultFloorId?: string
}

// The backend rejects the archive with distinct messages for unresolved
// incidents and for in-progress guard tours (ENG-32005); map each to an
// actionable toast rather than the generic failure copy.
const getArchiveErrorKey = (message: string | undefined) => {
  if (message?.includes('unresolved incidents')) {
    return 'floors:archive_error_has_incidents' as const
  }
  if (message?.includes('guard tours')) {
    return 'floors:archive_error_has_guard_tours' as const
  }
  return 'floors:archive_error' as const
}

export const useArchiveFloor = () => {
  const { t } = useTranslation()
  const { showSuccess, showError } = useToasts()
  const [archiveFloorMutation, { loading: isArchiveLoading }] =
    useArchiveFloorMutation()
  const [unarchiveFloorMutation, { loading: isUnarchiveLoading }] =
    useUnarchiveFloorMutation()

  const archiveFloor = async ({
    floorId,
    newDefaultFloorId,
  }: ArchiveFloorArgsIProps): Promise<boolean> => {
    try {
      await archiveFloorMutation({
        variables: {
          input: {
            id: floorId,
            ...(newDefaultFloorId ? { newDefaultFloorId } : {}),
          },
        },
        refetchQueries: [
          'Facilities',
          'FacilitiesTable',
          'facilityFloorsDrawer',
          'FacilityArchivedFloors',
          'floorsByFacilityIds',
          'FacilityFloorPlan',
          'floorPlanDetails',
        ],
      })
      showSuccess(t('floors:archive_success'))
      return true
    } catch (error) {
      const message =
        error instanceof ApolloError ? error.graphQLErrors[0]?.message : ''
      showError(t(getArchiveErrorKey(message)))
      return false
    }
  }

  const unarchiveFloor = async (floorId: string): Promise<boolean> => {
    try {
      await unarchiveFloorMutation({
        variables: { input: { id: floorId } },
        refetchQueries: [
          'Facilities',
          'FacilitiesTable',
          'facilityFloorsDrawer',
          'FacilityArchivedFloors',
          'floorsByFacilityIds',
          'FacilityFloorPlan',
          'floorPlanDetails',
        ],
      })
      showSuccess(t('floors:unarchive_success'))
      return true
    } catch {
      showError(t('floors:unarchive_error'))
      return false
    }
  }

  return { archiveFloor, isArchiveLoading, unarchiveFloor, isUnarchiveLoading }
}
